import {
  RuleStyle,
  ThreeDPositionDataMapping,
  ThreeDPositionStyle,
} from "@/shared/domain/layouts/LayoutRecord";
import { LoggerService } from "@/shared/services/LoggerService";
import {
  type File,
  type State,
  type PanelState,
  type TopicData,
  CURRENT_VIZ_SCHEMA_VERSION,
  PanelType,
  LogPanelState,
  ImagePanelState,
  MapPanelState,
  PlotPanelState,
  RawMessagePanelState,
} from "@/shared/state/visualization";
import {
  Datasource,
  PanelStates,
  ThreeDPanelState,
} from "@/shared/state/visualization/schema";
import * as threeDSchema from "@/shared/state/visualization/schema/v1/three-d";
import { schema as vizSchema } from "@/shared/state/visualization/schema/v1/v1";

import { InvalidVizConfig } from "./exception";

interface PanelData {
  data: TopicData[];
}

/**
 * Representation of visualization state responsible for serialization, deserialization,
 * and providing a convenient interface for reading visualization data.
 */
export class VizConfig {
  readonly #state: State;

  /**
   * Create an empty visualization state.
   */
  public static default(): State {
    return {
      version: CURRENT_VIZ_SCHEMA_VERSION,
      files: [],
      datasources: [],
      layout: {
        id: "root",
        axis: "x",
        relativeSize: 1,
        children: [],
      },
      panels: {},
    };
  }

  public static from_obj(obj: unknown): VizConfig {
    VizConfig.normalizeRawMessagePanels(obj);

    if (VizConfig.isValid(obj)) {
      return new VizConfig(obj);
    }

    LoggerService.error("Unable to instantiate VizConfig:", obj);
    throw new InvalidVizConfig("Unable to instantiate VizConfig");
  }

  /**
   * Backward compatibility: RawMessage panels used to store `data` as
   * `TopicData | null`. The current schema requires `TopicData[]`.
   * Normalize in-place before schema validation so that persisted
   * workspace configs from before the multi-path change still load.
   */
  private static normalizeRawMessagePanels(obj: unknown): void {
    if (
      typeof obj !== "object" ||
      obj === null ||
      !("panels" in obj) ||
      typeof (obj as Record<string, unknown>).panels !== "object" ||
      (obj as Record<string, unknown>).panels === null
    ) {
      return;
    }

    const panels = (obj as Record<string, unknown>).panels as Record<
      string,
      Record<string, unknown>
    >;
    for (const panel of Object.values(panels)) {
      if (panel.type !== PanelType.RawMessage) {
        continue;
      }
      if (panel.data === null || panel.data === undefined) {
        panel.data = [];
      } else if (!Array.isArray(panel.data)) {
        panel.data = [panel.data];
      }
    }
  }

  public static isValid(state: unknown): state is State {
    return vizSchema.safeParse(state).success;
  }

  /**
   * Returns the topic data for a specific panel, organized based on the panel type.
   */
  static getDataForPanel(panel: PanelState): PanelData[] {
    return exhaustiveSwitch<PanelType, PanelData[]>(panel.type, {
      [PanelType.Log]: () => {
        const logPanel = panel as LogPanelState;
        return logPanel.data.map((data) => ({ data: [data] }));
      },
      [PanelType.Image]: () => {
        const imagePanel = panel as ImagePanelState;
        return imagePanel.data.map((clip) => ({ data: [clip.data] }));
      },
      [PanelType.Plot]: () => {
        const plotPanel = panel as PlotPanelState;
        return plotPanel.data.map((series) => ({ data: [series.data] }));
      },
      [PanelType.Map]: () => {
        const mapPanel = panel as MapPanelState;
        return mapPanel.data.map((mapPath) => ({ data: mapPath.data }));
      },
      [PanelType.ThreeD]: () => {
        const threeDPanel = panel as ThreeDPanelState;
        return Object.values(threeDPanel.data.entities).map((e) => ({
          data: [
            ...extractTopicDataFromPosition(e.position).topicData,
            // A model's joints bind topics too, and this is what tells the
            // workspace which topics a panel reads.
            ...extractTopicDataFromMesh(e.mesh),
            ...extractTopicDataFromPointCloud(e.pointCloud),
          ],
        }));
      },
      [PanelType.RawMessage]: () => {
        const rawMessagePanel = panel as RawMessagePanelState;
        return rawMessagePanel.data.map((data) => ({ data: [data] }));
      },
      [PanelType.Document]: () => {
        // Document panel doesn't use topic data
        return [];
      },
    });
  }

  /**
   * Returns the data and style information for a specific panel's data items.
   */
  static getDataAndStylesForPanel(panel: PanelState): {
    data: TopicData[];
    style: RuleStyle | undefined;
  }[] {
    return exhaustiveSwitch<
      PanelType,
      { data: TopicData[]; style: RuleStyle | undefined }[]
    >(panel.type, {
      [PanelType.Log]: () => {
        const logPanel = panel as LogPanelState;
        return logPanel.data.map((data) => ({
          data: [data],
          style: undefined,
        }));
      },
      [PanelType.Image]: () => {
        const imagePanel = panel as ImagePanelState;
        return imagePanel.data.map((clip) => ({
          data: [clip.data],
          style: undefined,
        }));
      },
      [PanelType.Plot]: () => {
        const plotPanel = panel as PlotPanelState;
        return plotPanel.data.map((series) => ({
          data: [series.data],
          style: {
            color: series.config?.lineColor,
            // Only set the field on the object at all if it is defined.
            ...(series.config?.lineWidth !== undefined
              ? { lineWidth: series.config.lineWidth }
              : {}),
            ...(series.config?.pointRadius !== undefined
              ? { pointRadius: series.config.pointRadius }
              : {}),
            ...(series.config?.yAxis !== undefined
              ? { yAxis: series.config.yAxis }
              : {}),
            ...(series.config?.label !== undefined
              ? { label: series.config.label }
              : {}),
            ...(series.config?.transform !== undefined
              ? { transform: series.config.transform }
              : {}),
          },
        }));
      },
      [PanelType.Map]: () => {
        const mapPanel = panel as MapPanelState;
        return mapPanel.data.map((mapPath) => ({
          data: mapPath.data,
          style: {
            color: mapPath.style?.lineColor,
            // Persist the user's custom label so it survives a save/load round-trip.
            ...(mapPath.style?.label !== undefined
              ? { label: mapPath.style.label }
              : {}),
          },
        }));
      },
      [PanelType.ThreeD]: () => {
        const threeDPanel = panel as ThreeDPanelState;
        return Object.values(threeDPanel.data.entities).map((e) => {
          const {
            topicData: positionTopicData,
            dataMapping: positionDataMapping,
          } = extractTopicDataFromPosition(e.position);
          const meshTopicData = extractTopicDataFromMesh(e.mesh);
          return {
            // A model's joints are bound inside its mesh rather than through a
            // position, so harvesting only the position channels would make an
            // articulated robot look requirement-free to the layout matcher —
            // and `LayoutRuleMatcher` drops an entity with no requirements.
            // `ruleStyleSchema.threeDComponents.mesh` already carries the
            // bindings themselves verbatim, so they survive the round trip; what
            // was missing was the topics they name. A point cloud is often an
            // entity's only binding, so the same applies to its source.
            data: [
              ...positionTopicData,
              ...meshTopicData,
              ...extractTopicDataFromPointCloud(e.pointCloud),
            ],
            style: {
              threeDComponents: {
                entityId: e.id,
                position: positionDataMapping,
                rotation: e.rotation,
                mesh: e.mesh,
                path: e.path,
                vectorField: e.vectorField,
                pointCloud: e.pointCloud,
                // The cloud's source is harvested last, after the position's
                // axes and the joints' topics.
                pointCloudTopicDataIndex: e.pointCloud
                  ? positionTopicData.length + meshTopicData.length
                  : undefined,
              },
            } satisfies RuleStyle,
          };
        });
      },
      [PanelType.RawMessage]: () => {
        const rawMessagePanel = panel as RawMessagePanelState;
        return rawMessagePanel.data.map((data) => ({
          data: [data],
          style: undefined,
        }));
      },
      [PanelType.Document]: () => {
        // Document panel doesn't use topic data
        return [];
      },
    });
  }

  /**
   * Returns all topic data for a panel as a flat array.
   */
  static getTopicDataForPanel(panel: PanelState): TopicData[] {
    return this.getDataForPanel(panel).flatMap(({ data }) => data);
  }

  public constructor(state?: State) {
    this.#state = state ? this.withDatasources(state) : VizConfig.default();
  }

  public get files(): File[] {
    return this.#state.files;
  }

  public get datasources(): Datasource[] {
    return this.#state.datasources ?? [];
  }

  public get layout() {
    return this.#state.layout;
  }

  public get panels(): {
    [id: string]: PanelStates;
  } {
    return this.#state.panels ?? {};
  }

  public getPanelById(id: string): PanelState | undefined {
    return this.#state.panels?.[id];
  }

  public hasPanels(): boolean {
    return Object.keys(this.#state.panels ?? {}).length > 0;
  }

  public iterPanels(): IterableIterator<PanelState> {
    return Object.values(this.#state.panels ?? {})[Symbol.iterator]();
  }

  public toObject(): State {
    return this.#state;
  }

  public toString(): string {
    return JSON.stringify(this.#state, null, 2);
  }

  private withDatasources(state: State): State {
    return {
      ...state,
      // Backward compatibility: if datasources is not provided, use files
      datasources:
        state.datasources ??
        state.files.map((f) => ({
          type: "file",
          value: f,
        })),
    };
  }
}

type EnumCases<T extends string | number | symbol, R> = {
  [K in T]: () => R;
};
function exhaustiveSwitch<T extends string | number | symbol, R>(
  enumValue: T,
  cases: EnumCases<T, R>,
): R {
  return cases[enumValue]();
}

/**
 * The topics a mesh binds, which today means a model's joint values.
 *
 * Ordered by joint name rather than by insertion, so the harvested list is
 * stable across a save/load round trip of the record backing `jointValues`.
 */
function extractTopicDataFromMesh(
  mesh: threeDSchema.Mesh | undefined,
): TopicData[] {
  if (mesh?.type !== "model") {
    return [];
  }
  return Object.keys(mesh.jointValues ?? {})
    .sort()
    .flatMap((name) => {
      const value = (mesh.jointValues ?? {})[name].value;
      return typeof value === "number" ? [] : [value];
    });
}

/** The one topic a point cloud binds: the `data` path it reads its bytes from. */
function extractTopicDataFromPointCloud(
  pointCloud: threeDSchema.PointCloud | undefined,
): TopicData[] {
  return pointCloud ? [pointCloud.source] : [];
}

function extractTopicDataFromPosition(p: threeDSchema.Position | undefined): {
  topicData: TopicData[];
  dataMapping?: ThreeDPositionStyle;
} {
  if (!p) {
    return { topicData: [] };
  }
  const topicData = [p.forward, p.right, p.up]
    .filter((maybeTopicData) => maybeTopicData)
    .filter((maybeTopicData) => typeof maybeTopicData !== "number");
  const dataMappingFor = (
    value: TopicData | number,
    scale: number,
  ): ThreeDPositionDataMapping => {
    const index = topicData.findIndex((data) => data === value);
    if (index >= 0) {
      return {
        type: "topicData",
        topicDataIndex: index,
        scale,
      };
    }
    // If the topicdata list did not contain value, then it is a number.
    return {
      type: "fixed",
      value: value as number,
      scale,
    };
  };
  return {
    topicData,
    dataMapping: {
      transformation: p.transformation,
      basePreset: p.basePreset,
      right: dataMappingFor(p.right, p.rightScale),
      forward: dataMappingFor(p.forward, p.forwardScale),
      up: dataMappingFor(p.up, p.upScale),
    },
  };
}
